import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Loader from "./components/preloader/Loaders";
import NavBar from "./components/navbar/NavBar";
import CircularMenu from "./components/Custom/CustomContextMenu";
import { AnimatePresence } from 'framer-motion';
import HomePage from "./pages/HomePage.pages";
import AboutPage from "./pages/AboutPage.pages";
import SkillsPage from "./pages/SkillsPage.pages";
import ProjectPage from "./pages/ProjectPage.pages";
import ContactPage from "./pages/ContactPage.pages";
import StarFall from "./components/icons/menuCustom/StarFall";
import MeteorIcons from "./components/icons/menuCustom/MeteorIcons";
import SpiderIcons from "./components/icons/menuCustom/SpiderIcons";
import InspectIcons from "./components/icons/menuCustom/InspectIcons";
import GameIcons from "./components/icons/menuCustom/GameIcons";
import ReactDOMServer from 'react-dom/server';
import TelephoneBoot from "./components/icons/CurserIcons/TelephoneBoot";
import StreetLight from "./components/icons/CurserIcons/StreetLight";
import FireHydrant from "./components/icons/CurserIcons/FireHydrant";
import BigBenClock from "./components/icons/CurserIcons/BigBenClock";
import BigDipper from "./components/icons/CurserIcons/BigDipper";
import BB8 from "./components/icons/CurserIcons/BB8";
import Atronaut from "./components/icons/CurserIcons/Atronaut";
import LaserBlast from "./components/icons/CurserIcons/LaserBlast";
import LaserGun from "./components/icons/CurserIcons/LaserGun";
import Torch from "./components/icons/CurserIcons/Torch";
import Pencil from "./components/icons/CurserIcons/Pencil";
import MagicWard1 from "./components/icons/CurserIcons/MagicWand1";
import MagicWand2 from "./components/icons/CurserIcons/MagicWand2";
import PaintBrush from "./components/icons/CurserIcons/PaintBrush";
import FeatherDusterWipe from "./components/icons/CurserIcons/FeatherDusterWipe";
import Orbit from "./components/icons/CurserIcons/Orbit";
import Ufo from "./components/icons/CurserIcons/Ufo";
import PlanetUniverse from "./components/icons/CurserIcons/PlanetUniverse";
import Glow from "./components/icons/CurserIcons/Glow";
import Alien from "./components/icons/CurserIcons/Alien";
import Sword from "./components/icons/CurserIcons/Sword";
import Comet from "./components/icons/CurserIcons/Comet";
import StarStruck from "./components/icons/CurserIcons/StarStruck";
import Notification from "./components/Notification/Notification";

// Function to convert SVG component to data URL
const svgToDataUrl = (svgComponent) => {
  const svgString = ReactDOMServer.renderToStaticMarkup(svgComponent);
  return `url('data:image/svg+xml;base64,${btoa(svgString)}') 16 16, auto`;
};

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [toggle, setToggle] = useState(true);
  const [backgroundEffect, setBackgroundEffect] = useState("meteor");
  const [lastCursorIndex, setLastCursorIndex] = useState(null);
  const [noti, setNoti] = useState(false); // Set initial state to false
  const navigate = useNavigate();

  useEffect(()=>{
    document.title = "Kailash Agarwal";
  },[])

  // Array of SVG components as cursors
  const cursorIcons = [
    svgToDataUrl(<BB8 />),
    svgToDataUrl(<Atronaut />),
    svgToDataUrl(<BigDipper />),
    svgToDataUrl(<BigBenClock />),
    svgToDataUrl(<StreetLight />),
    svgToDataUrl(<TelephoneBoot />),
    svgToDataUrl(<FireHydrant />),
    svgToDataUrl(<LaserBlast />),
    svgToDataUrl(<LaserGun />),
    svgToDataUrl(<Torch />),
    svgToDataUrl(<Pencil />),
    svgToDataUrl(<MagicWard1 />),
    svgToDataUrl(<MagicWand2 />),
    svgToDataUrl(<PaintBrush />),
    svgToDataUrl(<FeatherDusterWipe />),
    svgToDataUrl(<Orbit />),
    svgToDataUrl(<Ufo />),
    svgToDataUrl(<PlanetUniverse />),
    svgToDataUrl(<Glow />),
    svgToDataUrl(<Alien />),
    svgToDataUrl(<Sword />),
    svgToDataUrl(<Comet />),
    svgToDataUrl(<StarStruck />)
  ];

  const changeCursorIcon = () => {
    let randomIndex;
    do {
      randomIndex = Math.floor(Math.random() * cursorIcons.length);
    } while (randomIndex === lastCursorIndex); // Ensure different selection

    const selectedCursorIcon = cursorIcons[randomIndex];
    document.body.style.cursor = selectedCursorIcon || 'auto';
    setLastCursorIndex(randomIndex); // Store the current selection as the last one
  };

  // Menu options for circular menu
  const menuOptions = [
    { label: 'Meteor_Shower', icon: (<MeteorIcons />), onClick: () => setBackgroundEffect('meteor') },
    { label: 'Star_Falling', icon: (<StarFall toggle={toggle} setToggle={setToggle} />), onClick: () => setBackgroundEffect('stars') },
    { label: 'Spider', icon: (<SpiderIcons />), onClick: () => setBackgroundEffect('spider') },
    { label: 'Inspect', icon: (<InspectIcons toggle={toggle} setToggle={setToggle} />), onClick: changeCursorIcon },
    { label: 'Contact', icon: (<GameIcons toggle={toggle} setToggle={setToggle} />), onClick: () => navigate("/game") }
  ];

  // Loading effect
  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 2000); // 2 seconds loading time
  }, []);

  // Notification effect after 10 seconds
  useEffect(() => {
    setTimeout(() => {
      setNoti(true); // Show the notification after 10 seconds
    }, 10000); // 10000 milliseconds = 10 seconds
  }, []);

  return (
    <div>
      <AnimatePresence mode="wait">
        {isLoading && <Loader />}
      </AnimatePresence>
      <div className="h-screen overflow-y-scroll snap-y snap-mandatory scroll-smooth">
        {noti && <Notification message="Thank you for visiting my portfolio! This site is thoughtfully crafted to showcase my expertise in web development, designed with care to offer a seamless and engaging user experience." />} {/* Notification triggered after 10 seconds */}
        <NavBar toggle={toggle} setToggle={setToggle} />
        <section className="snap-normal" id="home">
          <HomePage toggle={toggle} setToggle={setToggle} backgroundEffect={backgroundEffect} />
        </section>
        <section className="snap-normal" id="about">
          <AboutPage toggle={toggle} setToggle={setToggle} />
        </section>
        <section className="snap-normal" id="skills">
          <SkillsPage toggle={toggle} setToggle={setToggle} />
        </section>
        <section className="snap-normal" id="projects">
          <ProjectPage toggle={toggle} setToggle={setToggle} />
        </section>
        <section className="snap-normal" id="contact">
          <ContactPage toggle={toggle} setToggle={setToggle} />
        </section>
      </div>

      <CircularMenu menuOptions={menuOptions} toggle={toggle} setToggle={setToggle} />
    </div>
  );
}

export default App;
